import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useGpsSharing } from '../context/GpsSharingContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { MapPin, Radio, StopCircle, Truck, Satellite, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ChauffeurPage = () => {
    const { token, user } = useAuth();
    const { sharing, frequency, lastPosition, sentCount, gpsError, startSharing, stopSharing, changeFrequency } = useGpsSharing();
    const [vehicles, setVehicles] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchVehicles = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/vehicles/my-vehicle`, { headers: { Authorization: `Bearer ${token}` } });
            setVehicles(res.data);
        } catch {
            toast.error('Erreur de chargement des vehicules');
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => { fetchVehicles(); }, [fetchVehicles]);

    if (loading) {
        return <div className="flex items-center justify-center h-64"><div className="spinner"></div></div>;
    }

    return (
        <div className="space-y-6 max-w-2xl mx-auto" data-testid="chauffeur-page">
            <div className="page-header">
                <h1 className="page-title">Mode Chauffeur</h1>
                <p className="page-subtitle">Partagez la position GPS de votre vehicule en temps reel</p>
            </div>

            {sharing && (
                <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-sm text-emerald-400 flex items-center gap-2" data-testid="sharing-persist-info">
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse flex-shrink-0"></span>
                    Le partage reste actif meme si vous naviguez vers d'autres pages de l'application.
                </div>
            )}

            {vehicles.length === 0 ? (
                <Card className="tactical-card">
                    <CardContent className="p-8 text-center space-y-3" data-testid="no-vehicle-message">
                        <Truck className="w-12 h-12 text-zinc-600 mx-auto" />
                        <p className="text-zinc-300 font-medium">Aucun vehicule ne vous est assigne comme conducteur</p>
                        <p className="text-sm text-zinc-500">Demandez au gestionnaire des moyens de vous assigner comme conducteur d'un vehicule ({user?.name}).</p>
                    </CardContent>
                </Card>
            ) : vehicles.map(vehicle => {
                const isSharing = sharing === vehicle.id;
                return (
                    <Card key={vehicle.id} className={`tactical-card ${isSharing ? 'border-emerald-500/60' : ''}`} data-testid={`chauffeur-vehicle-${vehicle.id}`}>
                        <CardContent className="p-6 space-y-5">
                            <div className="flex items-center justify-between flex-wrap gap-2">
                                <div className="flex items-center gap-3">
                                    <Truck className="w-8 h-8 text-blue-400" />
                                    <div>
                                        <p className="text-lg font-bold text-white">{vehicle.name}</p>
                                        <p className="text-sm text-zinc-400">{vehicle.registration || vehicle.type}</p>
                                    </div>
                                </div>
                                {isSharing && (
                                    <span className="flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-400 text-sm font-semibold" data-testid="live-badge">
                                        <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse"></span> EN DIRECT
                                    </span>
                                )}
                            </div>

                            {/* Frequence */}
                            <div className="flex items-center gap-3">
                                <span className="text-sm text-zinc-400">Frequence d'envoi :</span>
                                {[10, 30].map(f => (
                                    <button
                                        key={f}
                                        onClick={() => changeFrequency(f)}
                                        data-testid={`frequency-${f}s`}
                                        className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${frequency === f ? 'bg-blue-500/20 border-blue-500/60 text-blue-300' : 'border-zinc-700 text-zinc-400 hover:border-zinc-500'}`}
                                    >
                                        {f}s
                                    </button>
                                ))}
                            </div>

                            {/* Bouton principal */}
                            {isSharing ? (
                                <Button onClick={() => stopSharing()} className="w-full h-16 text-lg bg-red-600 hover:bg-red-700 text-white" data-testid="stop-sharing-btn">
                                    <StopCircle className="w-6 h-6 mr-2" /> Arreter le partage
                                </Button>
                            ) : (
                                <Button onClick={() => startSharing(vehicle.id, frequency, vehicle.name)} disabled={!!sharing} className="w-full h-16 text-lg bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="start-sharing-btn">
                                    <Radio className="w-6 h-6 mr-2" /> Demarrer le partage de position
                                </Button>
                            )}

                            {gpsError && isSharing && (
                                <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm" data-testid="gps-error">
                                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" /> {gpsError}
                                </div>
                            )}

                            {/* Statut */}
                            {isSharing && lastPosition && (
                                <div className="grid grid-cols-2 gap-3 text-sm" data-testid="sharing-status">
                                    <div className="p-3 rounded-lg bg-zinc-800/60">
                                        <p className="text-zinc-500 text-xs flex items-center gap-1"><MapPin className="w-3 h-3" /> Derniere position</p>
                                        <p className="text-white font-mono text-xs mt-1">{lastPosition.lat.toFixed(5)}, {lastPosition.lon.toFixed(5)}</p>
                                        <p className="text-zinc-500 text-xs mt-0.5">Precision ~{lastPosition.accuracy}m</p>
                                    </div>
                                    <div className="p-3 rounded-lg bg-zinc-800/60">
                                        <p className="text-zinc-500 text-xs flex items-center gap-1"><Satellite className="w-3 h-3" /> Envois</p>
                                        <p className="text-white font-semibold mt-1">{sentCount} position(s)</p>
                                        <p className="text-zinc-500 text-xs mt-0.5">Dernier: {lastPosition.time.toLocaleTimeString('fr-FR')}</p>
                                    </div>
                                </div>
                            )}

                            {isSharing && (
                                <p className="text-xs text-zinc-500 text-center">Gardez cet onglet navigateur ouvert. L'ecran est maintenu allume automatiquement si votre telephone le permet.</p>
                            )}
                        </CardContent>
                    </Card>
                );
            })}
        </div>
    );
};

export default ChauffeurPage;
